import { Auth } from "aws-amplify";
import { Component, FormEvent, FunctionComponent } from "react";
import { Form, Alert, Card, Row, Col } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { RouteComponentProps } from "react-router-dom";
import { ContactDetails, ISetShopContactDetails } from "../../../client/core";
import { Color, Path, Storage } from "../../../env";
import { Button, icon, variant } from "../../form/Button";

export interface SetShopContactDetailsState {
    error: string;
    isLoading: boolean;
    isEmailValid?: boolean;
    isPhoneValid?: boolean;
    contactDetails: ContactDetails;
}

export interface SetShopContactDetailsProps extends RouteComponentProps {
    coreAPI: ISetShopContactDetails;
    auth: typeof Auth;
}

export default class ShopContactDetails extends Component<
    SetShopContactDetailsProps,
    SetShopContactDetailsState
> {
    constructor(props: SetShopContactDetailsProps) {
        super(props);
        this.state = {
            isLoading: false,
            error: "",
            contactDetails: {
                email: "",
                phone: "",
            },
        };
    }

    handleSubmit = () => {
        this.setState({
            isLoading: true,
        })
        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            return this.setState({
                isLoading: false,
                error: "Your shop could not be found - please navigate back to the shop dashboard to resync",
            });
        }

        if (!this.state.contactDetails.email || this.state.contactDetails.email.trim() === "") {
            return this.setState({
                isLoading: false,
                error: "Please enter your customer support email address",
            });
        }

        if (!this.state.contactDetails.phone || this.state.contactDetails.phone.trim() === "") {
            return this.setState({
                isLoading: false,
                error: "Please enter your customer support phone number",
            });
        }
        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();
            this.props.coreAPI
                .SetShopContactDetails({
                    identityToken: token,
                    shopID: shopID,
                    contactDetails: {
                        email: this.state.contactDetails.email,
                        phone: this.state.contactDetails.phone,
                    },
                })
                .then((output) => {
                    if (output.statusCode === 403) {
                        localStorage.removeItem(Storage.ShopID);
                        this.props.auth.signOut();
                        return this.props.history.push(Path.Login)
                    }
                    if (output.statusCode == 400) {
                        return this.setState({
                            isLoading: false,
                            error: "Please double check the contact details entered. It appears at least one of the fields is invalid."
                        })
                    }
                    return this.props.history.push(Path.RegisterShopSocials);
                })
                .catch(() =>
                    this.setState({
                        isLoading: false,
                        error: "We were unable to set your contact details at this time.",
                    })
                );
        }).catch(() => {
            localStorage.removeItem(Storage.ShopID);
            this.props.auth.signOut();
            return this.props.history.push(Path.Login)
        })
    };

    handlePhoneChange = (value: string): void => {
        const validatePhone = (value: string) => /^\d{7,15}$/.test(value);
        const isValid = validatePhone(value)
        this.setState({
            isPhoneValid: isValid,
            contactDetails: {
                ...this.state.contactDetails,
                phone: value
            }
        })
    }

    handleEmailChange = (value: string): void => {
        const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        const isValid = validateEmail(value);
        this.setState({
            isEmailValid: isValid,
            contactDetails: {
                ...this.state.contactDetails,
                email: value
            }
        })
    }

    render() {
        const props: SetShopContactDetailsFormProps = {
            error: this.state.error,
            email: this.state.contactDetails.email,
            phone: this.state.contactDetails.phone,
            isEmailValid: this.state.isEmailValid,
            isPhoneValid: this.state.isPhoneValid,
            isLoading: this.state.isLoading,
            onSubmit: this.handleSubmit,
            onEmailChange: this.handleEmailChange,
            onPhoneChange: this.handlePhoneChange,
        };
        return <SetShopContactDetailsForm {...props} />;
    }
}

export interface SetShopContactDetailsFormProps {
    error: string;
    email: string;
    phone: string;
    isLoading: boolean;
    isEmailValid?: boolean;
    isPhoneValid?: boolean;
    onSubmit: () => void;
    onPhoneChange: (value: string) => void
    onEmailChange: (value: string) => void
}

export const SetShopContactDetailsForm: FunctionComponent<SetShopContactDetailsFormProps> = (
    props
) => (
    <Form>
        <h1 style={{ marginBottom: "2rem" }}>Enter Contact</h1>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <Row>
            <Col sm={12}>
                <Form.Group className="mb-3" controlId="formEmail">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control type="email" isValid={props.isEmailValid} placeholder="Email" onChange={(e: any) => props.onEmailChange(e.target.value)} required />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPhone">
                    <Form.Label>Phone number</Form.Label>
                    <Form.Control type="text" isValid={props.isPhoneValid} placeholder="Phone Number" onChange={(e: any) => props.onPhoneChange(e.target.value)} />
                </Form.Group>
            </Col>
            <Button
                style={{ marginTop: "2rem", width: "100%" }}
                id={"set-contact-details-btn"}
                buttonvariant={variant.Primary}
                name={"Continue"}
                disabled={props.isLoading}
                icon={(props.isLoading) ? icon.Spinner : undefined}
                onClick={() => props.onSubmit()}
            />
        </Row>
    </Form>
);